import {
  SVGDatumType,
  dmyFormatter,
  getMoneyFormatter,
  rateFormatter,
} from '..'

import { TickFormatter, TickLabelProps } from '@visx/axis'

import { ScaleBand, ScaleLinear } from 'd3'

import { MultiChartData, MultiChartProps } from './typings'
import { getMergedLabels } from '../lib'
import { Dictionary } from '@percept/types'
import { produceKeyedMapping } from '@percept/utils'
import { MultiChartTooltipData, MultiChartTooltipDatum } from './MultiChartTooltip'
import { flatten, zip } from 'lodash-es'


export type LegendConfiguration = {
  label: string
  x: number
  width: number
  color: string
}


export const getScaleBandMidPointAccessor = <T extends SVGDatumType>(
  scale: ScaleBand<number>
) => (d: T): number => (scale(d.label as number) || 0) + (scale.bandwidth() / 2)

export const getLinearScaleRatePercentageAccessor = <T extends SVGDatumType>(
  scale: ScaleLinear<number, number>
) => (d: T): number => scale(d.value || 0)

export const invertBandScale = (scale: ScaleBand<number>, value: number): number => {
  const domain = scale.domain()
  const paddingOuter = scale(domain[0]) || 0
  const eachBand = scale.step()
  const index = Math.floor(((value - paddingOuter) / eachBand))
  return domain[Math.max(0, Math.min(index, domain.length - 1))]
}

export const normaliseDomain = (domain: [number, number]): [number, number] => {
  if( !domain[1] ){
    return [0, 1]
  }
  return [0, domain[1]]
}

const getMatchingDatasetData = <T extends SVGDatumType>({
  label,
  valueFormatter,
  datasets,
}: {
  label: string | number
  valueFormatter?: TickFormatter<any>
  datasets: MultiChartData<T>[]
}): MultiChartTooltipDatum[] => {
  const matchedData: MultiChartTooltipDatum[] = []
  datasets.forEach( dataset => {
    dataset.data.forEach( d => {
      if( d.label === label ){
        matchedData.push({
          label: dataset.label,
          color: dataset.color,
          value: valueFormatter ? valueFormatter(d.value, 0, []) : d.value,
        })
      }
    })
  })
  return matchedData
}

export const getMultiChartTooltipData = <T extends SVGDatumType>({
  label,
  yTickFormatterBarChart,
  yTickFormatterLineChart,
  xTickFormatter = dmyFormatter,
  barChartDatasets,
  lineChartDatasets,
}: {
  label: string | number
} & Pick<
  MultiChartProps<T>,
  'barChartDatasets' | 'lineChartDatasets' | 'yTickFormatterBarChart' | 'yTickFormatterLineChart' | 'xTickFormatter'
>): MultiChartTooltipData => {
  const barData = barChartDatasets && getMatchingDatasetData({
    label,
    valueFormatter: yTickFormatterBarChart,
    datasets: barChartDatasets,
  })
  const lineData = lineChartDatasets && getMatchingDatasetData({
    label,
    valueFormatter: yTickFormatterLineChart,
    datasets: lineChartDatasets,
  })
  const matchedData = flatten(zip(barData, lineData)).filter(Boolean) as MultiChartTooltipDatum[]
  return {
    label: xTickFormatter(label, 0, []),
    data: matchedData,
  }
}

export const getDatasetTotalsByLabel = <T extends SVGDatumType>(datasets: MultiChartData<T>[]): Record<string, number> => (
  datasets.reduce( (acc, dataset) => {
    dataset.data.forEach( (datum) => {
      acc[datum.label] = acc[datum.label] || 0
      acc[datum.label] += Number(datum.value)
    })
    return acc
  }, {} as Record<string, number>)
)


export const getBarGroupData = <T extends SVGDatumType>(datasets: MultiChartData<T>[]): Dictionary[] => {
  const datasetDataByLabel = datasets.reduce( (acc, dataset) => {
    dataset.data.forEach( d => {
      acc[d.label] = acc[d.label] || {}
      acc[d.label][dataset.key] = d.value
    })
    return acc
  }, {} as Record<string, Record<string, number | null>>)
  const labels = getMergedLabels(datasets)
  return labels.map( label => {
    const datasetMap = datasetDataByLabel[label as string]
    return {
      label,
      ...datasetMap,
    }
  })
}

export const getColorAccessor = <T extends SVGDatumType>(datasets: MultiChartData<T>[]): ((key: string) => string) => {
  const mapping = produceKeyedMapping(datasets, 'key')
  return (key: string) => mapping[key].color
}

export const getBarGroupDataLabel = (d: Dictionary): number => (
  Number(d.label)
)


const tickFontSize = 11
const tickFontWeight = 400

export const getXTickLabelProps: TickLabelProps<number> = () => ({
  dominantBaseline: 'central',
  textAnchor: 'middle',
  fontWeight: tickFontWeight,
  fontSize: tickFontSize,
  fill: 'currentColor',
})

export const getPrimaryYTickLabelProps: TickLabelProps<{ valueOf: () => number }> = () => ({
  dx: -6,
  dominantBaseline: 'central',
  textAnchor: 'end',
  fontWeight: tickFontWeight,
  fontSize: tickFontSize,
  fill: 'currentColor',
})

export const getSecondaryYTickLabelProps: TickLabelProps<{ valueOf: () => number }> = () => ({
  dx: 6,
  dominantBaseline: 'central',
  textAnchor: 'start',
  fontWeight: tickFontWeight,
  fontSize: tickFontSize,
  fill: 'currentColor',
})

export const getSpendTickFormatter = (currency: string) => (
  getMoneyFormatter(currency) as unknown as TickFormatter<{ valueOf: () => number }>
)

export const percentageRateTickFormatter = (
  rateFormatter as unknown as TickFormatter<{ valueOf: () => number }>
)

export const getMarkerUrl = (key: string): string => (
  `marker-${key.replace(/^[^a-z]+|[^\w:.-]+/gi, '')}`
)
